import {setAuthToken} from "@utils/lib/tokens";
import axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import {toast} from "react-toastify";

const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL
})

const errorHandler = (error) => {
	if(error?.response?.status && error?.response?.status !== 404 && error?.response?.status !== 403 && error?.response?.status !== 500){
		toast.error(`${error?.response?.data?.message || 'Возникла ошибка'}`);  
	}

  return Promise.reject({ ...error })
}

instance.interceptors.response.use(
  (response) => {return response},
  (error) => errorHandler(error)
);


function getAccessToken() {
	return localStorage.getItem('token');
}

instance.interceptors.request.use((request) => {
	if (!request.headers['Authorization']){
		request.headers['Authorization'] = `Bearer ${getAccessToken()}`;
	}
	return request;
});

// Single-flight обеспечивает axios-auth-refresh: параллельные 401 ждут один общий refresh-call.
const refreshAuthLogic = async failedRequest => {
	// 401 при логине — это неверный телефон/пароль, рефреш не нужен
	if (failedRequest.config?.url === '/api/login') {
		return Promise.reject(failedRequest);
	}

	try {
		// skipAuthRefresh: 401/403 от самого /api/refresh — финал, не ретраим его
		const { data } = await instance.post('/api/refresh', null, { skipAuthRefresh: true });
		const token = data.authorisation.token;

		// Сразу заменяем сохранённый токен — старый после этого невалиден
		localStorage.setItem('token', token);
		failedRequest.response.config.headers.Authorization = `Bearer ${token}`;
	} catch (error) {
		// 403 — аккаунт забанен: показываем сообщение сервера
		if (error?.response?.status === 403) {
			toast.error(error?.response?.data?.message || 'Аккаунт заблокирован');
		}
		auth.logout();
		throw error;
	}
};

createAuthRefreshInterceptor(instance, refreshAuthLogic, {statusCodes: [403, 401]});

// HTTP-действия из карточек чата (message.card.actions: {method, url})
export const cardAction = (method, url) => instance.request({ method, url });

export const auth = {
	getMaintenance() {
		return instance.get('/api/maintenance')
	},
	login(loginPayload, teacher_id) {
		return instance.post("/api/login", loginPayload).then(response => {
				if(response.data.status === 'success'){
					const token  =  response.data.authorisation.token;
					localStorage.setItem("token", token);
					if(teacher_id){
						localStorage.setItem('teacher_id', teacher_id)
					}
					setAuthToken(token);
					window.ym(96194074,'reachGoal','sign-in-user')
					window.location.href = '/'
				}
			})
	},
	forgotPassword(data) {
		return instance.post("/api/forgot-password", data)
	},
	resetPasswordRequest(data){
		return instance.post("/api/auth/reset-password/request", data).then(res => {
			if(res?.data){
				localStorage.setItem("resend_sms_available_at", res?.data?.resend_available_at)
			}
			
			return res 
		})
	},
	resetPasswordVerify(data){
		return instance.post("/api/auth/reset-password/verify", data)
	},
	resetPasswordReset(data){
		return instance.post("/api/auth/reset-password/reset", data)
	},
	resetPassword(data) {
		return instance.post("/api/reset-password", data)
	},
	verifyEmail(token) {
		return instance.post("/api/verify-email/" + token)
	},
	unsubscribe(token) {
		return instance.post("/api/unsubscribe-email-notifications/" + token)
	},
	register(payload, teacher_id) {
		return instance.post("/api/register", payload).then(response => {
			const token  =  response.data.authorisation.token;
			localStorage.setItem("token", token);
			if(teacher_id){
				localStorage.setItem("teacher_id", teacher_id);
			}
			setAuthToken(token);
			window.ym(96194074,'reachGoal','sign-up-user')
			window.location.href = '/'
		})
	},
	requestPhoneVerificationCode() {
		return instance.post("/api/request-phone-verification-code")
	},
	phoneVerificationCode(data) {
		return instance.post("/api/verify-phone-code", data)
	},
	registerTeacher(payload) {
		return instance.post("/api/register/teacher", payload)
	},
	me() {
		return instance.get("/api/me")
	},
	logout() {
		// Инвалидируем токен на сервере (fire-and-forget: сервер может уже считать его протухшим)
		instance.post('/api/logout', null, { skipAuthRefresh: true }).catch(() => {});
		localStorage.removeItem("token");
		setAuthToken();
		window.ym(96194074,'reachGoal','logout')
		window.location.href = '/login'
	}
}

export const profile = {
	getAllInstruments() {
		return instance.get('/api/instruments/all')
	},
	getOnboardNeeded() {
		return instance.get('/api/profile/onboarding-needed')
	},
	getAvailableInstruments() {
		return instance.get('/api/instruments/available')
	},
	getAnalytics() {
		return instance.get('/api/profile/analytics')
	},
	onboardingPassed(data) {
		return instance.post('/api/profile/onboarding-passed', data)
	},
	updateProfileAvatar(data) {
		const formData = new FormData()
		formData.append('avatar', data);
	
		return instance.post('/api/profile/update-avatar', formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		});
	},
	async updatePhotos(data) {
		const attachments = await uploadFiles(data?.attachments)

		return instance.post('/api/profile/update-photos', {photos: [...attachments, ...data?.photos]});
	},
	getAlerts() {
		return instance.get('/api/profile/alerts')
	},
	async updateProfile(data) {
		if(data?.attachments){
			const attachments = await uploadFiles(data?.attachments)
			const newData =  (({ attachments, ...o }) => o)(data)

			return instance.post("/api/profile/update", {...newData, education_proof: [...attachments, ...data?.education_proof]})
		}else{
			return instance.post("/api/profile/update", {...data})
		}
	}, 
	updateEmail(data) {
		return instance.post("/api/profile/update-email", {...data})
	},
	updateEmailResend(data) {
		return instance.post("/api/profile/update-email/resend", {...data})
	},
	confirmEmail(data) {
		return instance.post("/api/verify-email", {...data})
	},
	getEmailResendAvailable() {
		return instance.get("/api/profile/update-email/resend-available")
	},
	updateGrade(data) {
		return instance.post("/api/profile/update-grade", {...data})
	},
	getAvailableGrades() {
		return instance.post("/api/profile/get-available-grades")
	},
	updateInstruments(data) {
		return instance.post("/api/profile/update", {...data})
	}, 
	updatePassword(data) {
		return instance.post("/api/profile/update-password", {...data})
	}, 
	updateProfileNotifications(data){
		return instance.post("/api/profile/update-notifications", {...data})
	},
	updateVacation(data) {
		return instance.post("/api/profile/vacation", {...data})
	},
	updateVideoPresentation(data) {
		const formData = new FormData()
		formData.append('video_presentation', data);
		let toastId = null
	
		return instance.post('/api/profile/video-presentation/attach', formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			onUploadProgress: (processEvent) => {
				const {loaded, total} = processEvent
				let progress = loaded / total
				let percents = Math.floor(progress * 100)

				if (toastId === null) {
					toastId = toast('Загрузка видео ' + percents + '%', { type: 'success', progress, isLoading: percents < 100 });
				} else {
					toast.update(toastId, { render: 'Загрузка видео ' + percents + '%', progress });
				}
			}
		}).finally(() => {
			if (toastId !== null) {
				toast.dismiss(toastId);
			}
		});
	},
	removeVideoPresentation() {
		return instance.post('/api/profile/video-presentation/detach')
	},
	fillInformation(data) {
		return instance.post("/api/profile/fill-up", data)
			.then(response => {
				const token = response.data.authorisation.token;
				localStorage.setItem("token", token);
				setAuthToken(token);
				window.location.href = '/'
			}).catch(err => {
				toast.error(err?.response?.data?.message)
			})
	},
	addCard() {
		return instance.get(process.env.REACT_APP_PAYMENT_URL + '/add-card')
	},
	removeCard() {
		return instance.post(process.env.REACT_APP_PAYMENT_URL + '/remove-card')
	},

	getAllPayouts(params) {
		return instance.get('/api/appointments/payouts', {params: {...params}})
	},
	// Прикрепление чека к занятию (занятие из заказа: items[0].service_id при service_type === 'appointment')
	sendReceipt({appointment_id, data}){
		const formData = new FormData()
		formData.append('receipt_file', data);
		let toastId = null
	
		return instance.post(`/api/appointments/${appointment_id}/attach-receipt`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			onUploadProgress: (processEvent) => {
				const {loaded, total} = processEvent
				let progress = loaded / total
				let percents = Math.floor(progress * 100)

				if (toastId === null) {
					toastId = toast('Загрузка чека ' + percents + '%', { type: 'success', progress, isLoading: percents < 100 });
				} else {
					toast.update(toastId, { render: 'Загрузка чека ' + percents + '%', progress });
				}
			}
		}).finally(() => {
			if (toastId !== null) {
				toast.dismiss(toastId);
			}
		});
	},
	updateDiscountAvailable(data) {
		return instance.post('/api/profile/financials/update', data)
	},
	getAllTeachers(params) {
		return instance.get('/api/teachers/search', {params: {...params}})
	},
	getTimeSlots() {
		return instance.get('/api/profile/timeslots')
	},
	updateTimeSlots(data) {
		return instance.post('/api/profile/timeslots/update', data)
	},
	detachTelegram() {
		return instance.post('/api/profile/detach-telegram')
	},
	updateUserPreference(data) {
		return instance.post('/api/profile/update-student-preferences', data)
	},
	signDocsRequest() {
		return instance.post('/api/profile/docs/sign-request')
	},
	signDocs(data) {
		return instance.post('/api/profile/docs/sign', data)
	}
}

const  uploadFiles = async (files) => {
	let uploadPromises = files?.map(file => {
			const formData = new FormData();
			formData.append('attachment', file);

			let toastId = null
			return instance.post('/api/attachments/upload', formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				},
				onUploadProgress: (processEvent) => {
					const {loaded, total} = processEvent
					let progress = loaded / total
					let percents = Math.floor(progress * 100)

					if (toastId === null) {
						toastId = toast('Загрузка файла ' + percents + '%', { type: 'success', progress, isLoading: percents < 100 });
					} else {
						toast.update(toastId, { render: 'Загрузка файла ' + percents + '%', progress });
					}
				}
			}).finally(() => {
				if (toastId !== null) {
					toast.dismiss(toastId);
				}
			});
	});

	const uploadResponses = await Promise.all(uploadPromises || []);
	const attachments = uploadResponses?.map(response => response.data?.attachment.id) || []; // id of attachments

	return attachments
}

export const appointment = {
	async createNote(data){
		const attachments = await uploadFiles(data?.attachments)

		return instance.post(`/api/appointments/${data.appointmentId}/notes/create`, {
			text: data.data.text,
			attachments: [...(data?.filesId || []), ...attachments]
		})
	},
	getNote(data){
		return instance.get(`/api/appointments/${data.appointmentId}/notes/${data.noteId}`)
	},
	// Лента материалов: kind = 'notes' | 'homework'. Без cursor — текущее занятие, с cursor — прошлые.
	getMaterialsFeed({ appointmentId, kind, cursor, limit }){
		return instance.get(`/api/appointments/${appointmentId}/${kind}`, {
			params: {
				...(cursor ? { cursor } : {}),
				...(limit ? { limit } : {}),
			}
		})
	},
	async editNote(data){
		const attachments = await uploadFiles(data?.attachments)

		return instance.post(`/api/appointments/${data.appointmentId}/notes/${data.noteId}/edit`, {
			text: data.data.text,
			attachments: [...(data?.filesId || []), ...attachments]
		})
	},
	removeNote(data){
		return instance.delete(`/api/appointments/${data.appointmentId}/notes/${data.noteId}/delete`)
	},
	async createHomework(data){
		const attachments = await uploadFiles(data?.attachments)

		return instance.post(`/api/appointments/${data.appointmentId}/homework/create`, {
			task: data.data.task,
			attachments: [...(data?.filesId || []), ...attachments]
		})
	},
	getHomework(data){
		return instance.get(`/api/appointments/${data.appointmentId}/homework/${data.homeworkId}`)
	},
	async editHomework(data){
		const attachments = await uploadFiles(data?.attachments)
		
		return instance.post(`/api/appointments/${data.appointmentId}/homework/${data.homeworkId}/edit`, {
			task: data.data.task,
			attachments: [...(data?.filesId || []), ...attachments]
		})
	},
	getRecord(id){
		return instance.get(`/api/appointments/${id}/download`)
	},
	removeHomework(data){
		return instance.delete(`/api/appointments/${data.appointmentId}/homework/${data.homeworkId}/delete`)
	},
	async submitHomework(data){
		const attachments = await uploadFiles(data?.attachments)

		return instance.post(`/api/appointments/${data.appointmentId}/homework/${data.homeworkId}/submissions/submit`, {
			text: data.data.text,
			attachments: [...(data?.filesId || []), ...attachments]
		})
	},
	getSubmission(data){
		return instance.get(`/api/appointments/${data.appointmentId}/homework/${data.homeworkId}/submissions/${data.submissionId}`)
	},
	rejectHomework(data){
		return instance.post(`/api/appointments/${data.appointmentId}/homework/${data.homeworkId}/submissions/${data.submissionId}/reject`, {
			comment: data.comment
		})
	},
	approveHomework(data){
		return instance.post(`/api/appointments/${data.appointmentId}/homework/${data.homeworkId}/submissions/${data.submissionId}/approve`)
	},

}

export const classes = {
	getClassById(id, config) {
		return instance.get(`/api/appointments/${id}`, config)
	},
	getClassTokenById(id) {
		return instance.get(`/api/appointments/${id}/join-token`)
	},
	getAllClasses(params) {
		return instance.get('/api/appointments', {params: params})
	},
	getAllOrders() {
		return instance.get('/api/orders/unpaid')
	},
	getOrderById(id) {
		return instance.get('/api/orders/' + id)
	},
	cancelOrder(id) {
		return instance.post(`/api/orders/${id}/cancel`)
	},
	getUpcomingClasses() {
		return instance.get('/api/appointments/upcoming')
	},
	getHistory(page){
		return instance.get('/api/appointments/history', {params: {page}})
	},
	cancelClass({id, reason}){
		return instance.post(`/api/appointments/${id}/cancel`)
	},
	attachAppointmentReceipt({id, data}){
		const formData = new FormData()
		formData.append('receipt_file', data)
		return instance.post(`/api/appointments/${id}/attach-receipt`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		})
	},
	payForAppointment(id) {
		return instance.post(process.env.REACT_APP_PAYMENT_URL + '/pay/' + id)
	},
	createClass(data) {
		return instance.post('/api/appointments/create', data)
	},
	createClassByTeacher(studentId, time, date, instrument) {
		return instance.post('/api/appointments/create', {
			"student_id": studentId,
			"date": date,
			"time": time,
			"duration": "1h",
			"instrument_id": instrument
		})
	},
	getTeacher(slug) {
		return instance.get(`/api/teachers/${slug}`)
	},
	getSchedule(slug, params) {
		return instance.get(`/api/teachers/${slug}/availability`, {params: {...params}})
	},
	discountAvailable(id) {
		return instance.get(`/api/teachers/${id}/discount-available`)
	},
	getReviews(slug) {
		return instance.get(`/api/teachers/${slug}/feedbacks`)
	},
	getLandingReviews(id) {
		return instance.get(`/api/landing/teachers/${id}/reviews`)
	},
	addReview(body, id) {
		return instance.post(`/api/appointments/${id}/feedback`, body)
	},
	addTeacherReview(body, id) {
		return instance.post(`/api/teachers/${id}/feedbacks`, body)
	},
	getEnabledPromocode(body) {
		return instance.get(`/api/appointments/price`, {params: body}).catch(({response}) => {
			return null
		})
	}
}

export const chat = {
	postAttachment(data) {
		const formData = new FormData()
		formData.append('attachment', data);
	
		let toastId = null
		return instance.post('/api/attachments/upload', formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			onUploadProgress: (processEvent) => {
					const {loaded, total} = processEvent
					let progress = loaded / total
					let percents = Math.floor(progress * 100)

					if (toastId === null) {
						toastId = toast('Загрузка файла ' + percents + '%', { type: 'success', progress, isLoading: percents < 100 });
					} else {
						toast.update(toastId, { render: 'Загрузка файла ' + percents + '%', progress });
					}
				}
			}).finally(() => {
				if (toastId !== null) {
					toast.dismiss(toastId);
				}
		})
	},
	// TODO: заменить на актуальный endpoint бэкенда
	searchChats(query) {
		return instance.get('/api/chats/search', { params: { query } })
	},
	createChat(body) {
		return instance.post('/api/chats/create', body)
	}
}

export const downloadDocApi = (url) => {
	
	return instance.get(url, { responseType: 'blob' })
}

export const referral = {
	getMyReferrals() {
		return instance.get('/api/referrals')
	},
	getReferralsLevels() {
		return instance.get('/api/referrals/levels')
	}
}


export const students = {
	getMyStudents(params) {
		return instance.get('/api/profile/students', {params: params})
	},
	getStudentById(id){
		return instance.get('/api/profile/students/' + id)
	},
	getIsStudentOnService(email){
		return instance.get('/api/profile/students/check-on-service', {data: {email}})
	},
	connectStudent(data){
		return instance.post('/api/profile/students/invite', data).then(res => {
			if(!res?.data?.student_id){
				toast.success('Приглашение отправлено')
			}
			
			return res
		})
	},
	updateStudent(data){
		return instance.post('/api/profile/students/'+ data.id +'/update', {outside_payments: data.outside_payments})
	},
	disconnectStudentById(id){
		return instance.delete('/api/profile/students/' + id)
	},
	isOutsidePayments(id){
		return instance.get('/api/teachers/' + id + '/outside-payment-available')
	}
}


export const packages = {
	getPacks() {
		return instance.get('/api/packages')
	},
	getActivePacks() {
		return instance.get('/api/packages/active')
	},
	getArchivedPacks() {
		return instance.get('api/packages/archived')
	},
	getTeacherAvaiablePacks(teacher) {
		return instance.get(`api/teachers/${teacher}/package-offers`)
	},
	bookingPack(id) {
		return instance.post('api/packages/purchase', {offer_id: id})
	},
	cancelPack(id) {
		return instance.post(`api/packages/${id}/cancel`, {})
	},
	getProfilePackages() {
		return instance.get('/api/profile/packages')
	},
	createProfilePackage(payload) {
		return instance.post('/api/profile/packages/create', payload)
	},
	updateProfilePackage({ id, ...payload }) {
		return instance.post(`/api/profile/packages/${id}/update`, payload)
	},
	deleteProfilePackage(id) {
		return instance.delete(`/api/profile/packages/${id}`)
	}
}
export const attachments = {
	list() {
		return instance.get('/api/attachments')
	},
	upload(data) {
		return chat.postAttachment(data)
	},
	remove(id) {
		return instance.delete(`/api/attachments/${id}`)
	},
}
